import axiosWrapper from "../../axios.config";
import axios from "axios";
import AuthUjwtHelper from "../helpers/authUjwtHelper";
import { normalizeDomainNameForApi } from "../helpers/domainHelper";
import {
  getApiUrl,
  getChatUrl,
  getAiChatUrl,
  getPaletteUrl,
  getCreateSiteWizardUrl,
  getUpdateSiteWizardUrl,
  getGeneratePageDataUrl,
  getCreateProjectUrl,
  getSfcoreUrl,
  getProjectsTokenUrl,
  getGetLoginUrl,
  getIds4UjwtUrl,
  getProjectLimitsUrl,
  getSfcoreAccessKey,
  getSfcoreAccessKeyDomains,
  getSfAmUrl,
  getSiteplusAccountsMfeUrl,
  getCheckGridLayoutUrl,
  getSinglePageRenderUrl,
  getOnboardingProgressUrl,
  getAiTonesUrl,
  getProvisionedResourcesUrl,
  getStudioApiUrl,
  getImageLibraryUrl,
  getSfamToken,
} from "../helpers/envHelper";
import { getCookieValue } from "../helpers/index.js";
import { imageStringToFile } from "../helpers/imageHelper.js";

export const getOnboardingData = async () => {
  const response = await axios.get(`${getApiUrl()}/onboarding`);
  return response.data;
};

export const saveOnboardingData = async (data) => {
  const response = await axios.post(`${getApiUrl()}/onboarding`, data);
  return response.data;
};

export const getContentStructure = async ({ data }) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.post(getChatUrl(), data, { headers });
  return response.data;
};

export const getTemplateCategory = async ({ data }) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.post(getAiChatUrl(), data, { headers });
  return response.data;
};

export const getTemplatePalette = async (templateId) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.get(`${getPaletteUrl()}/${templateId}`, {
    headers,
  });
  return response.data;
};

export const fixJson = async ({ data }) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.post(getChatUrl(), data, { headers });
  return response.data;
};

export const createSite = async (data) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.post(getCreateSiteWizardUrl(), data, {
    headers,
  });
  return response.data;
};

export const finalizeSite = async (data, siteId) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.put(
    `${getUpdateSiteWizardUrl()}/${siteId}`,
    data,
    { headers },
  );
  return response.data;
};

/** Full sitewizard update (template / onboarding payload), same URL as finalize with different body. */
export const updateSiteWizard = async (siteId, data) => {
  const headers = { 'Authorization': `Bearer ${await AuthUjwtHelper.checkAuth()}` };
  const response = await axios.put(`${getUpdateSiteWizardUrl()}/${siteId}`, data, { headers });
  return response.data;
};

/**
 * Triggers / returns AI page block data for a sitewizard.
 * POST to VITE_GENERATE_PAGE_DATA_URL with { accessToken, sitewizardId }.
 */
export const generatePageData = async ({ accessToken, sitewizardId }) => {
  const headers = { 'Authorization': `Bearer ${await AuthUjwtHelper.checkAuth()}` };
  const response = await axios.post(
    getGeneratePageDataUrl(),
    { accessToken, sitewizardId },
    { headers },
  );
  return response.data;
};

export const createProject = async (data, ids4ujwt) => {
  const headers = { Authorization: `Bearer ${ids4ujwt}` };
  const response = await axios.post(getCreateProjectUrl(), data, { headers });
  return response.data;
};

export const getEnhancedUjwt = async (pId) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const payload = {
    resourceUrn: `urn:jarvis:webcom:product-instance:${pId}`,
  };
  const response = await axios.post(
    `${getIds4UjwtUrl()}/ujwt/reissue`,
    payload,
    { headers },
  );
  return response.data?.UJWT ?? response.data?.ujwt;
};

export const getSiteplusToken = async (pId) => {
  const payload = {
    request: {
      productInstanceId: pId,
      requestInfo: {
        apiAccessKey: getSfcoreAccessKey(),
        clientId: "NSI",
        method: "getJWTTokenV1",
        service: "ProductAPI",
      },
    },
  };
  const response = await axiosWrapper.post(getSfcoreUrl(), payload, {
    withCredentials: true,
  });
  return response.data?.response?.data?.a_token;
};

export const getTokenForProjects = async (accessToken) => {
  const response = await axios.get(
    `${getProjectsTokenUrl()}?scope=user-fe&accessToken=${accessToken}`,
  );
  return response.data;
};

/**
 * Siteplus account MFE — pick primary domain row (same rules as link-in-bio-frontend `domains.js`).
 * @param {string} projectsToken — member / projects JWT (`getTokenForProjects` → `.token`)
 */
export const getSiteplusAccountsMfe = async (projectsToken) => {
  const response = await axios.get(`${getSiteplusAccountsMfeUrl()}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${projectsToken}`,
    },
  });

  const domains = response?.data?.subscriptions?.[0]?.domains;
  const lowerCaseDomains = domains?.map(({ domain, ...rest }) => ({
    domain: normalizeDomainNameForApi(domain),
    ...rest,
  }));
  const processedDomains =
    lowerCaseDomains
      ?.map((domainObj) => {
        if (domainObj.domain?.includes("mylinks.")) {
          return {
            ...domainObj,
            domain: domainObj.domain.replace("mylinks.", ""),
          };
        }
        return domainObj;
      })
      .filter(
        (domainObj) => !domainObj.domain?.includes("netsolhost.com"),
      ) ?? [];

  return processedDomains[0] ?? null;
};

/** SFcore DomainAPI — returns domain product instance id (`prodInstId`) for SFAM forms. */
export const getDomainOverview = async (domainName) => {
  const normalizedDomainName = normalizeDomainNameForApi(domainName);
  const payload = {
    request: {
      requestInfo: {
        service: "DomainAPI",
        method: "getDomainOverview",
        clientId: "AccountManager",
        apiAccessKey: getSfcoreAccessKeyDomains(),
      },
      domainName: normalizedDomainName,
      registrantPersonOrgId: 0,
    },
  };
  const response = await axiosWrapper.post(getSfcoreUrl(), payload, {
    withCredentials: true,
  });
  return response?.data?.response?.data?.prodInstId ?? null;
};

export const getLoginUrl = async (projectsToken) => {
  const headers = { Authorization: `Bearer ${projectsToken}` };
  const response = await axios.get(getGetLoginUrl(), { headers });
  return response.data;
};

export const getNumberOfTemplatesAllowed = async (token) => {
  const headers = { Authorization: `Bearer ${token}` };
  const response = await axios.get(getProjectLimitsUrl(), { headers });
  return response.data;
};

export const getGridLayout = async (token) => {
  const headers = { Authorization: `Bearer ${token}` };
  const response = await axios.get(`${getCheckGridLayoutUrl()}`, { headers });
  return response.data;
};

export const getOnboardingProgress = async (id) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.get(
    `${getOnboardingProgressUrl()}/onboarding-state/${id}`,
    {
      headers,
    },
  );

  return response.data;
};

export const createOnboardingProgress = async ({
  productInstanceId,
  title,
  currentStep,
  onboardingJson,
  ...rest
}) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.post(
    `${getOnboardingProgressUrl()}/onboarding-state`,
    {
      productInstanceId,
      title,
      currentStep,
      onboardingJson: JSON.stringify(onboardingJson),
      version: 1,
      ...rest,
    },
    {
      headers,
    },
  );

  return response.data;
};

export const updateOnboardingProgress = async ({
  id,
  productInstanceId,
  title,
  currentStep,
  onboardingJson,
  onboardingStatus,
  ...rest
}) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.put(
    `${getOnboardingProgressUrl()}/onboarding-state`,
    {
      id,
      productInstanceId,
      title,
      currentStep,
      onboardingStatus: onboardingStatus,
      onboardingJson: JSON.stringify(onboardingJson),
      version: 1,
      ...rest,
    },
    {
      headers,
    },
  );

  return response.data;
};

export const deleteOnboardingProgress = async ({ id }) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.put(
    `${getOnboardingProgressUrl()}/onboarding-state/delete/${id}`,
    {},
    {
      headers,
    },
  );

  return response.data;
};

export const getOnboardingChoices = async ({ productInstanceId }) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.get(
    `${getOnboardingProgressUrl()}/onboarding-state/choices`,
    {
      headers,
      params: {
        productInstanceId,
      },
    },
  );

  return response.data;
};

export const getOnboardingStatusFromStoredash = async (productInstanceId) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.get(
    `${getOnboardingProgressUrl()}/onboarding-state/store-onboarding`,
    {
      headers,
      params: {
        productInstanceId,
      },
    },
  );

  return response.data;
};

export const getAutocompleteSuggestions = async (query, sessionId) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.get(
    `${getOnboardingProgressUrl()}/google-places/autocomplete?input=${query}&sessionToken=${sessionId}`,
    {
      headers,
    },
  );

  return response.data;
};

export const getPlaceDetails = async (placeId, sessionId) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.get(
    `${getOnboardingProgressUrl()}/google-places/lookup?PlaceId=${placeId}&SessionId=${sessionId}`,
    {
      headers,
    },
  );

  return response.data;
};

export const getTonesData = async (payload) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.post(getAiChatUrl(), payload, { headers });
  return response.data;
};

export const saveAiTonesData = async (payload) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.post(
    `${getAiTonesUrl()}/onboarding-state-ai`,
    payload,
    { headers },
  );
  return response.data;
};

export const getAbandonedDraftId = async (productInstanceId) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.get(
    `${getOnboardingProgressUrl()}/onboarding-state?productInstanceId=${productInstanceId}`,
    {
      headers,
    },
  );

  return response?.data?.onboardingState?.onboardingSessionId;
};

export const getProvisionedResources = async () => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.get(`${getProvisionedResourcesUrl()}`, {
    headers,
  });
  return response.data;
};

export const updateCompanyName = async (productInstanceId, companyName) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const response = await axios.put(
    `${getOnboardingProgressUrl()}/onboarding-state/update-company-name?productInstanceId=${productInstanceId}`,
    {
      company_name: companyName,
    },
    { headers },
  );
  return response.data;
};

export const getSections = async (projectId, accessToken) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const timestamp = new Date().toISOString();

  const response = await axios.get(
    `${getSinglePageRenderUrl()}/${projectId}/sections?accessToken=${accessToken}&timestamp=${timestamp}`,
    { headers },
  );
  return response.data;
};

export const transferFOSProject = async (
  apiKey,
  projectId,
  productInstanceId,
) => {
  try {
    const response = await axios.post(
      `https://rest.edit.site/integration-api/project-gw/projects/transfer/${projectId}`,
      { productInstanceId },
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("[API] transferFOSProject failed:", error);
    throw new Error("Failed to transfer project. Please try again.");
  }
};

export const uploadLogoToStudio = async (logoUrl, fileName = "logo") => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  const LOGO_SIZE = 1024;
  const EXTERNAL_IMAGE_TYPE = {
    UNKNOWN: 0,
    UNSPLASH: 1,
    OTHER: 2,
  };
  try {
    const response = await axios.post(
      `${getStudioApiUrl()}/images/external_url`,
      {
        url: logoUrl,
        width: LOGO_SIZE,
        height: LOGO_SIZE,
        external_image_type: EXTERNAL_IMAGE_TYPE.OTHER,
        file_name: fileName,
      },
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("[API] uploadLogoToStudio failed:", error);
    throw error;
  }
};

export const updateProjectLogo = async (projectId, logoSrc) => {
  const headers = {
    Authorization: `Bearer ${await AuthUjwtHelper.checkAuth()}`,
  };
  try {
    const response = await axios.put(
      `${getUpdateSiteWizardUrl()}/${projectId}`,
      {
        blocks: {
          header: {
            logo: {
              src: logoSrc,
            },
          },
        },
      },
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("[API] updateProjectLogo failed:", error);
    throw error;
  }
};

export const uploadImageToImageLibrary = async (images) => {
  const productInstanceId = getCookieValue("_sp_productInstanceId");
  const token = await AuthUjwtHelper.checkAuth();

  const imageList = (Array.isArray(images) ? images : [images])
    .map((item) => (typeof item === "string" ? { image: item } : item))
    .filter(({ image }) => image);

  if (imageList.length === 0) {
    return;
  }

  try {
    const formData = new FormData();
    formData.append("originApplicationName", "WEBSITE_BUILDER");

    for (const { image, tag } of imageList) {
      formData.append("files", await imageStringToFile(image));
      if (tag) {
        formData.append("tags", tag);
      }
      if (image.startsWith("http")) {
        formData.append("originUrl", image);
      }
    }

    const response = await axios.post(
      `${getImageLibraryUrl()}/v1/images`,
      formData,
      {
        headers: {
          Authorization: `Basic ${getSfamToken()}`,
          Token: token,
        },
        params: {
          productInstanceId,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("[API] uploadImageToImageLibrary failed:", error);
    throw error;
  }
};

/**
 * GET SFAM domain form. `domainProductInstanceId` is SFcore domain `prodInstId`;
 * `siteplusProductInstanceId` is `_sp_productInstanceId` used only for {@link getSiteplusToken}.
 */
export const getDomainFormInfo = async (
  domainProductInstanceId,
  siteplusProductInstanceId,
) => {
  const apiToken = await getSiteplusToken(siteplusProductInstanceId);
  const response = await axios.get(
    `${getSfAmUrl()}/user/api/v1/forms/${domainProductInstanceId}`,
    {
      headers: {
        Authorization: `Basic ${getSfamToken()}`,
        token: `${apiToken}`,
      },
    },
  );
  return response.data;
};

/**
 * SFAM POST body for `/user/api/v1/forms/:id` — `prodInstanceId`, `domainName`, and `formData` from the
 * forwarded GET response (or edited copy); `status` is always `COMPLETED` on POST.
 *
 * @param {string} domainProductInstanceId — URL path id when `source.prodInstanceId` is absent
 * @param {object} [source] — original GET payload / draft (same keys)
 */
export const buildDomainFormPostBody = (domainProductInstanceId, source = {}) => {
  const domainName = source.domainName;
  const formData = Array.isArray(source.formData) ? source.formData : [];
  if (!domainName) {
    throw new Error(
      "[API] buildDomainFormPostBody: domainName is required on the forwarded payload",
    );
  }
  return {
    prodInstanceId: source.prodInstanceId ?? domainProductInstanceId,
    domainName,
    formData,
    status: "COMPLETED",
  };
};

/**
 * POST SFAM domain form. Forwards the GET-shaped payload, sending only the fields
 * {@link buildDomainFormPostBody} keeps.
 */
export const postDomainFormInfo = async (
  domainProductInstanceId,
  rawPayload,
  siteplusProductInstanceId,
) => {
  const body = buildDomainFormPostBody(domainProductInstanceId, rawPayload);
  const apiToken = await getSiteplusToken(siteplusProductInstanceId);
  const response = await axios.post(
    `${getSfAmUrl()}/user/api/v1/forms/${domainProductInstanceId}`,
    body,
    {
      headers: {
        Authorization: `Basic ${getSfamToken()}`,
        token: `${apiToken}`,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};
